import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  BarChart3,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Clock,
  Filter,
  Star,
  Target,
  TrendingUp,
  Zap,
} from 'lucide-react';
import { getAllStages } from '../../lib/leadPipelineRegistry';
import { getStageSLAByStageId } from '../../lib/slaCatalogueRegistry';

// ── helpers ──────────────────────────────────────────────────────────────────

const isAr = () => document.documentElement.lang === 'ar';

function extractTarget(kpi: string): string | null {
  const m = kpi.match(/\(target:\s*([^)]+)\)/i);
  return m ? m[1].trim() : null;
}

function stripMeta(kpi: string): string {
  return kpi
    .replace(/\s*\(target:[^)]+\)/i, '')
    .replace(/\s*\(benchmark[^)]+\)/i, '')
    .replace(/\s*\([^)]*tracking[^)]*\)/i, '')
    .trim();
}

// Classify KPI into category for colouring
function kpiCategory(kpi: string): { label: string; color: string; bg: string } {
  const lower = kpi.toLowerCase();
  if (lower.includes('time') || lower.includes('speed') || lower.includes('sla') || lower.includes('minute') || lower.includes('hour') || lower.includes('second')) {
    return { label: 'Speed / SLA', color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200' };
  }
  if (lower.includes('rate') && (lower.includes('conversion') || lower.includes('booking') || lower.includes('contact') || lower.includes('show') || lower.includes('qualified'))) {
    return { label: 'Conversion', color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200' };
  }
  if (lower.includes('satisfaction') || lower.includes('nps') || lower.includes('csat') || lower.includes('referral') || lower.includes('sentiment')) {
    return { label: 'Customer', color: 'text-violet-700', bg: 'bg-violet-50 border-violet-200' };
  }
  if (lower.includes('compliance') || lower.includes('completeness') || lower.includes('accuracy') || lower.includes('duplicate') || lower.includes('cadence')) {
    return { label: 'Quality', color: 'text-blue-700', bg: 'bg-blue-50 border-blue-200' };
  }
  if (lower.includes('finance') || lower.includes('approval') || lower.includes('cancellation') || lower.includes('deal') || lower.includes('quote') || lower.includes('trade')) {
    return { label: 'Finance', color: 'text-rose-700', bg: 'bg-rose-50 border-rose-200' };
  }
  return { label: 'Operational', color: 'text-gray-600', bg: 'bg-gray-50 border-gray-200' };
}

// Filter options
type FilterType = 'all' | 'Speed / SLA' | 'Conversion' | 'Customer' | 'Quality' | 'Finance' | 'Operational';

// ── Component ──────────────────────────────────────────────────────────────────

const KPIFramework = () => {
  const ar = isAr();
  const [expandedStage, setExpandedStage] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');

  const allStages = getAllStages();

  const filterOptions: { id: FilterType; label: string; labelAr: string; icon: React.ElementType; color: string }[] = [
    { id: 'all', label: 'All KPIs', labelAr: 'جميع المؤشرات', icon: BarChart3, color: 'text-gray-600' },
    { id: 'Speed / SLA', label: 'Speed / SLA', labelAr: 'السرعة / مستوى الخدمة', icon: Zap, color: 'text-amber-600' },
    { id: 'Conversion', label: 'Conversion', labelAr: 'التحويل', icon: TrendingUp, color: 'text-emerald-600' },
    { id: 'Quality', label: 'Quality', labelAr: 'الجودة', icon: CheckCircle2, color: 'text-blue-600' },
    { id: 'Customer', label: 'Customer', labelAr: 'العميل', icon: Star, color: 'text-violet-600' },
    { id: 'Finance', label: 'Finance', labelAr: 'التمويل', icon: Target, color: 'text-rose-600' },
  ];

  // Count total KPIs across all stages
  const totalKPIs = allStages.reduce((sum, s) => sum + s.kpis.length, 0);

  // Filter KPIs for a stage
  const filterKPIs = (kpis: string[]) => {
    if (activeFilter === 'all') return kpis;
    return kpis.filter(k => kpiCategory(k).label === activeFilter);
  };

  const toggleStage = (id: string) => {
    setExpandedStage(prev => prev === id ? null : id);
  };

  return (
    <div className="space-y-8 animate-fade-in">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Link to="/performance" className="text-gray-400 hover:text-gray-600 transition-colors">
              <ArrowLeft className="h-4 w-4 rtl:rotate-180" />
            </Link>
            <span className="text-gray-400 text-sm">/</span>
            <span className="text-sm text-gray-500">{ar ? 'إدارة الأداء' : 'Performance'}</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">
            {ar ? 'إطار مؤشرات الأداء الرئيسية' : 'KPI Framework'}
          </h1>
          <p className="mt-1 text-gray-500 text-sm">
            {ar
              ? `${totalKPIs} مؤشر أداء عبر ${allStages.length} مرحلة — مستخرجة مباشرة من سجل المسار`
              : `${totalKPIs} KPIs across ${allStages.length} pipeline stages — live from the pipeline registry`}
          </p>
        </div>

        {/* Stats row */}
        <div className="flex gap-3">
          <div className="bg-primary-50 border border-primary-200 rounded-xl px-4 py-2.5 text-center">
            <p className="text-2xl font-bold text-primary-800">{totalKPIs}</p>
            <p className="text-xs text-primary-600">{ar ? 'إجمالي المؤشرات' : 'Total KPIs'}</p>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-center">
            <p className="text-2xl font-bold text-gray-800">{allStages.length}</p>
            <p className="text-xs text-gray-500">{ar ? 'المراحل' : 'Stages'}</p>
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="h-4 w-4 text-gray-400 flex-shrink-0" />
        {filterOptions.map(f => (
          <button
            key={f.id}
            onClick={() => setActiveFilter(f.id)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-medium transition-all border ${
              activeFilter === f.id
                ? 'bg-primary-600 text-white border-primary-600 shadow-sm'
                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
            }`}
          >
            <f.icon className="h-3.5 w-3.5" />
            {ar ? f.labelAr : f.label}
          </button>
        ))}
      </div>

      {/* Stage KPI Accordion */}
      <div className="space-y-3">
        {allStages.map(stage => {
          const filtered = filterKPIs(stage.kpis);
          if (filtered.length === 0 && activeFilter !== 'all') return null;

          const isOpen = expandedStage === stage.meta.id;
          const sla = getStageSLAByStageId(stage.meta.id);

          return (
            <div
              key={stage.meta.id}
              className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
            >
              {/* Stage header (always visible) */}
              <button
                className="w-full flex items-center gap-3 px-5 py-4 text-left rtl:text-right hover:bg-gray-50 transition-colors"
                onClick={() => toggleStage(stage.meta.id)}
              >
                <span
                  className="w-8 h-8 rounded-lg text-white text-sm font-bold flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: stage.meta.color }}
                >
                  {stage.meta.order}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-900">
                    {ar ? stage.meta.titleAr : stage.meta.title}
                  </p>
                  <p className="text-xs text-gray-500">
                    {ar ? stage.meta.ownerAr : stage.meta.owner}
                    {sla && (
                      <span className="ml-2 rtl:mr-2 rtl:ml-0 text-amber-600">
                        · {ar ? 'الهدف:' : 'Target:'} {sla.targetDuration}
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="text-xs bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full font-medium">
                    {filtered.length} {ar ? 'مؤشر' : 'KPI'}{filtered.length !== 1 && !ar ? 's' : ''}
                  </span>
                  {isOpen
                    ? <ChevronUp className="h-4 w-4 text-gray-400" />
                    : <ChevronDown className="h-4 w-4 text-gray-400" />
                  }
                </div>
              </button>

              {/* KPI list (expandable) */}
              {isOpen && (
                <div className="border-t border-gray-100 divide-y divide-gray-50">
                  {filtered.map((kpi, idx) => {
                    const target = extractTarget(kpi);
                    const cleanKPI = stripMeta(kpi);
                    const cat = kpiCategory(kpi);
                    return (
                      <div key={idx} className="flex items-start gap-3 px-5 py-3 hover:bg-gray-50 transition-colors">
                        <span className={`mt-0.5 text-xs px-2 py-0.5 rounded-full border font-medium flex-shrink-0 ${cat.bg} ${cat.color}`}>
                          {ar ? cat.label : cat.label}
                        </span>
                        <p className="text-sm text-gray-800 flex-1 leading-relaxed">{cleanKPI}</p>
                        {target && (
                          <span className="text-xs font-bold text-gray-700 bg-white border border-gray-200 rounded-lg px-2.5 py-1 flex-shrink-0 whitespace-nowrap">
                            {target}
                          </span>
                        )}
                      </div>
                    );
                  })}
                  {/* Footer with links */}
                  <div className="px-5 py-3 bg-gray-50 flex items-center justify-between">
                    <Link
                      to={`/pipeline/stage/${stage.meta.id}`}
                      className="text-xs text-primary-600 hover:text-primary-800 font-medium flex items-center gap-1"
                    >
                      {ar ? 'عرض المرحلة' : 'View stage'}
                      <ChevronRight className="h-3 w-3" />
                    </Link>
                    {sla && (
                      <Link
                        to={`/sla-catalogue/stage/${sla.id}`}
                        className="text-xs text-amber-600 hover:text-amber-800 font-medium flex items-center gap-1"
                      >
                        <Clock className="h-3 w-3" />
                        {ar ? 'مستوى الخدمة' : 'SLA detail'}
                        <ChevronRight className="h-3 w-3" />
                      </Link>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
          {ar ? 'دليل الفئات' : 'Category Legend'}
        </p>
        <div className="flex flex-wrap gap-2">
          {[
            { label: 'Speed / SLA', color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200' },
            { label: 'Conversion', color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200' },
            { label: 'Quality', color: 'text-blue-700', bg: 'bg-blue-50 border-blue-200' },
            { label: 'Customer', color: 'text-violet-700', bg: 'bg-violet-50 border-violet-200' },
            { label: 'Finance', color: 'text-rose-700', bg: 'bg-rose-50 border-rose-200' },
            { label: 'Operational', color: 'text-gray-600', bg: 'bg-gray-50 border-gray-200' },
          ].map(cat => (
            <span key={cat.label} className={`text-xs px-3 py-1 rounded-full border font-medium ${cat.bg} ${cat.color}`}>
              {cat.label}
            </span>
          ))}
        </div>
      </div>

    </div>
  );
};

export default KPIFramework;
